import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import { hookFormZod } from '@ipfabric/ui-components/utils/hookForm/hookFormZod';
import { cliLimitSessions } from '@ipfabric/utils';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { z } from 'zod';
import type { DiscoverySettingsObject } from '../../model/DiscoverySettingsObject';

export type CliSessionsFieldValues = {
  cliSessionsEnabled: boolean;
  cliSessionsLimit: string;
};
export type CliSessionsOutputValues = {
  cliSessionsEnabled: boolean;
  cliSessionsLimit: number;
};

const CliSessionsField = (props: {
  form: UseFormReturn<CliSessionsFieldValues, unknown, CliSessionsOutputValues>;
}) => {
  const cliSessionsEnabled = useWatch({
    control: props.form.control,
    name: 'cliSessionsEnabled',
  });
  return (
    <>
      <div className='my-xs'>
        <FormSwitch
          control={props.form.control}
          name='cliSessionsEnabled'
          label='Maximum number of parallel sessions'
        />
      </div>
      <FormTextField
        control={props.form.control}
        type='number'
        name='cliSessionsLimit'
        disabled={!cliSessionsEnabled}
        inputWidthClassName='w-60'
      />
    </>
  );
};

export const cliSessions: FormSnippet<
  DiscoverySettingsObject,
  CliSessionsFieldValues,
  CliSessionsOutputValues
> = {
  render: ({ form }) => <CliSessionsField form={form} />,
  fromModel: (model, fieldValuesDraft) => {
    fieldValuesDraft.cliSessionsEnabled =
      model.cliSessionsLimit?.enabled ?? false;
    fieldValuesDraft.cliSessionsLimit =
      model.cliSessionsLimit?.limit?.toString() ?? '';
  },
  toModel: (transformedValues, modelDraft) => {
    modelDraft.cliSessionsLimit = {
      limit: transformedValues.cliSessionsLimit,
      enabled: transformedValues.cliSessionsEnabled,
    };
  },
  schema: z.object({
    cliSessionsEnabled: z.boolean(),
    cliSessionsLimit: hookFormZod.numberInputWith((num) =>
      num.min(
        cliLimitSessions.min,
        `Must be greater than ${cliLimitSessions.min}`,
      ),
    ),
  }),
};
